import { AvertissementSalle } from './AvertissementSalle.js';
import { useId, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Price, Switch, Textarea } from '@artosera/ui';
import { ArrowDown, ArrowUp, X } from 'lucide-react';
import {
  useSalle,
  useModifierSalle,
  useLienSalle,
  useRevoquerSalle,
  type SallePresentee,
  type CorpsSalle,
  type LienSalle,
} from '../../api/viewingRooms.js';
import { useContact } from '../../api/contacts.js';
import { ApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { ObjetCartel } from '../../composants/ObjetCartel.js';
import { PastilleStatut } from '../../composants/PastilleStatut.js';
import { useShellContext } from '../shell/context.js';
import { RechercheOeuvre } from '../ventes/RechercheOeuvre.js';
import {
  dateFinJour,
  dateSaisie,
  dateSalle,
  EtatDeSalle,
  texteSalle,
  VignetteSalle,
} from './salleUi.js';
import { LienSalleDialog } from './LienSalleDialog.js';
import styles from './ViewingRoomDetailPage.module.css';

export function ViewingRoomDetailPage() {
  const { id } = useParams();
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation();
  const fiche = useSalle(galerieSlug, id ?? null);
  if (fiche.isLoading) return <p role="status">{t('regie:viewingRooms.chargement')}</p>;
  if (!fiche.data) return <p role="alert">{t('regie:viewingRooms.erreurs.generique')}</p>;
  return <FicheSalle key={`${galerieSlug}/${id}`} salle={fiche.data} />;
}
function FicheSalle({ salle }: { salle: SallePresentee }) {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation();
  const champId = useId();
  const peutEcrire = galerieALaction(galerie, 'viewing-rooms:write');
  const contact = useContact(
    galerieSlug,
    galerieALaction(galerie, 'contacts:read') ? (salle.contact?.id ?? null) : null,
  );
  const modifier = useModifierSalle(galerieSlug);
  const generer = useLienSalle(galerieSlug);
  const revoquer = useRevoquerSalle(galerieSlug);
  const [lien, setLien] = useState<LienSalle | null>(null);
  const [confirmation, setConfirmation] = useState(false);
  const [recherche, setRecherche] = useState(false);
  const [ajout, setAjout] = useState<{ id: string; titre: string } | null>(null);
  const [protection, setProtection] = useState(false);
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [maintenant] = useState(() => new Date());
  const occupe = modifier.isPending || generer.isPending || revoquer.isPending;
  const echec = (e: unknown) => {
    const raison = e instanceof ApiError ? (e.details as { raison?: string } | null)?.raison : null;
    setErreur(
      raison === 'expiration_invalide' || raison === 'salle_expiree' ? raison : 'generique',
    );
  };
  const enregistrer = (corps: Partial<CorpsSalle>, ensuite?: () => void) => {
    setErreur(null);
    modifier.mutate({ id: salle.id, corps }, { onError: echec, onSuccess: ensuite });
  };
  const ordonner = (index: number, sens: number) => {
    const ids = salle.oeuvres.map((o) => o.id);
    const cible = index + sens;
    [ids[index], ids[cible]] = [ids[cible]!, ids[index]!];
    enregistrer({ oeuvres: ids });
  };
  const titreFr = salle.titre.fr;
  const hotePerdu =
    salle.hoteLien !== null &&
    salle.hoteLien !== `${galerieSlug}.artosera.gallery` &&
    !galerieAModule(galerie, 'site');
  return (
    <div className={styles.page} data-ui="viewing-room-detail">
      <header className={styles.entete}>
        <Link to={`/${galerieSlug}/viewing-rooms`}>{t('regie:viewingRooms.titre')}</Link>
        <h1>{texteSalle(salle.titre, i18n.language)}</h1>
        <div className={styles.actions}>
          <EtatDeSalle etat={salle.etat} />
          {salle.contact && (
            <Link to={`/${galerieSlug}/contacts/${salle.contact.id}`}>
              {salle.contact.nomAffiche}
            </Link>
          )}
        </div>
      </header>
      {erreur && (
        <p className={styles.erreur} role="alert">
          {t(`regie:viewingRooms.erreurs.${erreur}`)}
        </p>
      )}
      <div className={styles.disposition}>
        <div className={styles.colonne}>
          <section className={styles.panneau} aria-label={t('regie:viewingRooms.champs.texte')}>
            <form
              className={styles.formulaire}
              onSubmit={(event) => {
                event.preventDefault();
                const f = new FormData(event.currentTarget);
                enregistrer({
                  titre: { fr: String(f.get('titre')), en: String(f.get('titreEn')) },
                  texte: { fr: String(f.get('texte')), en: String(f.get('texteEn')) },
                });
              }}
            >
              <label className={styles.champ}>
                {t('regie:viewingRooms.champs.titre')}
                <Input
                  name="titre"
                  defaultValue={titreFr}
                  required
                  maxLength={200}
                  readOnly={!peutEcrire}
                />
              </label>
              <label className={styles.champ}>
                {t('regie:viewingRooms.champs.titreEn')}
                <Input
                  name="titreEn"
                  defaultValue={salle.titre.en ?? ''}
                  maxLength={200}
                  readOnly={!peutEcrire}
                />
              </label>
              <label className={styles.champ}>
                {t('regie:viewingRooms.champs.texte')}
                <Textarea
                  name="texte"
                  defaultValue={salle.texte?.fr ?? ''}
                  rows={3}
                  maxLength={5000}
                  readOnly={!peutEcrire}
                />
              </label>
              <label className={styles.champ}>
                {t('regie:viewingRooms.champs.texteEn')}
                <Textarea
                  name="texteEn"
                  defaultValue={salle.texte?.en ?? ''}
                  rows={2}
                  maxLength={5000}
                  readOnly={!peutEcrire}
                />
              </label>
              {peutEcrire && (
                <Button type="submit" variant="secondary" disabled={occupe}>
                  {t('regie:viewingRooms.enregistrer')}
                </Button>
              )}
            </form>
          </section>
          <section className={styles.panneau} aria-labelledby={`${champId}-oeuvres`}>
            <h2 id={`${champId}-oeuvres`}>
              {t('regie:viewingRooms.champs.oeuvres')}{' '}
              <span className={styles.nombre}>{salle.oeuvres.length} / 40</span>
            </h2>
            {salle.oeuvresManquantes > 0 && (
              <p role="status">
                {t('regie:viewingRooms.manquantes', { count: salle.oeuvresManquantes })}
              </p>
            )}
            <ol className={styles.oeuvres}>
              {salle.oeuvres.map((oeuvre, index) => (
                <li key={oeuvre.id} className={styles.oeuvre} data-ui="viewing-room-work">
                  <VignetteSalle
                    galerieSlug={galerieSlug}
                    mediaId={oeuvre.mediaPrincipalId}
                    taille="md"
                  />
                  <div className={styles.cartel}>
                    <ObjetCartel
                      titre={oeuvre.titre}
                      artiste={oeuvre.artisteNom}
                      annee={oeuvre.annee}
                    />
                    <PastilleStatut statut={oeuvre.statut} />
                    {salle.prixVisibles && oeuvre.prixPublicCentimes !== null && (
                      <Price
                        cents={oeuvre.prixPublicCentimes}
                        currency={oeuvre.devise}
                        lang={i18n.language.startsWith('en') ? 'en' : 'fr'}
                      />
                    )}
                  </div>
                  {peutEcrire && (
                    <div className={styles.ordre}>
                      <Button
                        variant="ghost"
                        disabled={occupe || index === 0}
                        aria-label={t('regie:viewingRooms.monterOeuvre', { titre: oeuvre.titre })}
                        onClick={() => ordonner(index, -1)}
                      >
                        <ArrowUp size={18} aria-hidden />
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={occupe || index === salle.oeuvres.length - 1}
                        aria-label={t('regie:viewingRooms.descendreOeuvre', {
                          titre: oeuvre.titre,
                        })}
                        onClick={() => ordonner(index, 1)}
                      >
                        <ArrowDown size={18} aria-hidden />
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={occupe || salle.oeuvres.length <= 1}
                        aria-label={t('regie:viewingRooms.retirerOeuvre', { titre: oeuvre.titre })}
                        onClick={() =>
                          enregistrer({
                            oeuvres: salle.oeuvres
                              .filter((o) => o.id !== oeuvre.id)
                              .map((o) => o.id),
                          })
                        }
                      >
                        <X size={18} aria-hidden />
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ol>
            {ajout ? (
              <div>
                <p>{ajout.titre}</p>
                <AvertissementSalle galerieSlug={galerieSlug} oeuvreId={ajout.id} />
                <Button
                  disabled={occupe}
                  onClick={() =>
                    enregistrer({ oeuvres: [...salle.oeuvres.map((o) => o.id), ajout.id] }, () =>
                      setAjout(null),
                    )
                  }
                >
                  {t('regie:viewingRooms.confirmerAjout')}
                </Button>
                <Button variant="secondary" onClick={() => setAjout(null)}>
                  {t('regie:viewingRooms.annuler')}
                </Button>
              </div>
            ) : null}
            {peutEcrire &&
              (recherche ? (
                <RechercheOeuvre
                  galerieSlug={galerieSlug}
                  onChoisir={(oeuvre) => {
                    if (
                      !occupe &&
                      salle.oeuvres.length < 40 &&
                      !salle.oeuvres.some((o) => o.id === oeuvre.id)
                    )
                      setAjout({ id: oeuvre.id, titre: oeuvre.titre });
                    setRecherche(false);
                  }}
                />
              ) : (
                <Button
                  variant="secondary"
                  disabled={occupe || salle.oeuvres.length >= 40}
                  onClick={() => setRecherche(true)}
                >
                  {t('regie:viewingRooms.ajouterOeuvre')}
                </Button>
              ))}
          </section>
        </div>
        <aside className={styles.colonne}>
          <section className={styles.panneau} aria-labelledby={`${champId}-lien`}>
            <h2 id={`${champId}-lien`}>{t('regie:viewingRooms.lien.titre')}</h2>
            {salle.hoteLien && (
              <p className={styles.hote}>
                {t('regie:viewingRooms.lien.hote', { hote: salle.hoteLien })}
              </p>
            )}
            {hotePerdu && (
              <p role="status">
                {t('regie:viewingRooms.lien.hotePerdu', { hote: salle.hoteLien })}
              </p>
            )}
            <form
              className={styles.formulaire}
              onSubmit={(event) => {
                event.preventDefault();
                const date = String(new FormData(event.currentTarget).get('expireLe'));
                enregistrer({
                  expireLe: new Date(
                    Math.min(
                      new Date(dateFinJour(date)).getTime(),
                      new Date(salle.creeLe).getTime() + 90 * 86_400_000,
                    ),
                  ).toISOString(),
                });
              }}
            >
              <label className={styles.champ}>
                {t('regie:viewingRooms.champs.expireLe')}
                <Input
                  key={salle.expireLe}
                  type="date"
                  name="expireLe"
                  defaultValue={dateSaisie(new Date(salle.expireLe))}
                  min={dateSaisie(maintenant)}
                  max={dateSaisie(new Date(new Date(salle.creeLe).getTime() + 90 * 86_400_000))}
                  required
                  readOnly={!peutEcrire}
                />
              </label>
              {peutEcrire && (
                <Button type="submit" variant="secondary" disabled={occupe}>
                  {t('regie:viewingRooms.enregistrer')}
                </Button>
              )}
            </form>
            <Switch
              id={`${champId}-prix`}
              label={t('regie:viewingRooms.champs.prixVisibles')}
              checked={salle.prixVisibles}
              disabled={!peutEcrire || occupe}
              onCheckedChange={(prixVisibles) => enregistrer({ prixVisibles })}
            />
            <p className={styles.meta}>
              {t(
                salle.protegee
                  ? 'regie:viewingRooms.protegee'
                  : 'regie:viewingRooms.sansProtection',
              )}
            </p>
            {peutEcrire && (
              <div className={styles.formulaire}>
                {protection ? (
                  <form
                    className={styles.formulaire}
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (
                        motDePasse.length < 8 ||
                        new TextEncoder().encode(motDePasse).length > 72
                      ) {
                        setErreur('motDePasse');
                        return;
                      }
                      enregistrer({ motDePasse }, () => {
                        setMotDePasse('');
                        setProtection(false);
                        modifier.reset();
                      });
                    }}
                  >
                    <label className={styles.champ}>
                      {t('regie:viewingRooms.champs.motDePasse')}
                      <Input
                        type="password"
                        value={motDePasse}
                        autoComplete="new-password"
                        minLength={8}
                        maxLength={72}
                        onChange={(e) => setMotDePasse(e.target.value)}
                        required
                      />
                    </label>
                    <Button type="submit" variant="secondary" disabled={occupe}>
                      {t('regie:viewingRooms.enregistrer')}
                    </Button>
                  </form>
                ) : (
                  <Button variant="secondary" disabled={occupe} onClick={() => setProtection(true)}>
                    {t(
                      salle.protegee
                        ? 'regie:viewingRooms.changerMotDePasse'
                        : 'regie:viewingRooms.proteger',
                    )}
                  </Button>
                )}
                {salle.protegee && (
                  <Button
                    variant="ghost"
                    disabled={occupe}
                    onClick={() => enregistrer({ motDePasse: null })}
                  >
                    {t('regie:viewingRooms.retirerMotDePasse')}
                  </Button>
                )}
                {salle.etat === 'expiree' ? (
                  <p>{t('regie:viewingRooms.lien.prolonger')}</p>
                ) : (
                  <Button
                    disabled={occupe}
                    loading={generer.isPending}
                    onClick={() => {
                      setErreur(null);
                      generer.mutate({ id: salle.id, recevoirLien: setLien }, { onError: echec });
                    }}
                  >
                    {t(
                      salle.etat === 'sans_lien'
                        ? 'regie:viewingRooms.lien.generer'
                        : 'regie:viewingRooms.lien.regenerer',
                    )}
                  </Button>
                )}
                {salle.etat === 'active' && (
                  <Button variant="ghost" disabled={occupe} onClick={() => setConfirmation(true)}>
                    {t('regie:viewingRooms.lien.revoquer')}
                  </Button>
                )}
              </div>
            )}
          </section>
          <section className={styles.panneau} aria-labelledby={`${champId}-ouvertures`}>
            <h2 id={`${champId}-ouvertures`}>{t('regie:viewingRooms.ouvertures.titre')}</h2>
            {salle.ouvertures.total === 0 ? (
              <p className={styles.meta}>{t('regie:viewingRooms.ouvertures.aucune')}</p>
            ) : (
              <>
                <div className={styles.compteurs}>
                  <strong>
                    {t('regie:viewingRooms.ouvertures.total', { count: salle.ouvertures.total })}
                  </strong>
                  <span>
                    {t('regie:viewingRooms.ouvertures.visiteurs', {
                      count: salle.ouvertures.visiteurs,
                    })}
                  </span>
                </div>
                <dl className={styles.dates}>
                  {salle.ouvertures.premiereLe && (
                    <>
                      <dt>{t('regie:viewingRooms.ouvertures.premiere')}</dt>
                      <dd>{dateSalle(salle.ouvertures.premiereLe, i18n.language, true)}</dd>
                    </>
                  )}
                  {salle.ouvertures.derniereLe && (
                    <>
                      <dt>{t('regie:viewingRooms.ouvertures.derniere')}</dt>
                      <dd>{dateSalle(salle.ouvertures.derniereLe, i18n.language, true)}</dd>
                    </>
                  )}
                </dl>
                <ol className={styles.journal}>
                  {salle.ouvertures.journal.slice(0, 10).map((o, index) => (
                    <li key={`${o.date}-${index}`}>
                      <time dateTime={o.date}>{dateSalle(o.date, i18n.language, true)}</time>
                      <code>{o.visiteur}</code>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </section>
        </aside>
      </div>
      {lien && (
        <LienSalleDialog
          {...lien}
          titre={texteSalle(salle.titre, i18n.language)}
          contactEmail={contact.data?.contact.emails[0] ?? null}
          onFermer={() => {
            setLien(null);
            generer.reset();
          }}
        />
      )}
      <Dialog
        open={confirmation}
        onOpenChange={setConfirmation}
        title={t('regie:viewingRooms.lien.revoquer')}
        description={t('regie:viewingRooms.lien.confirmerRevocation')}
        closeLabel={t('regie:viewingRooms.fermer')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmation(false)}>
              {t('regie:viewingRooms.annuler')}
            </Button>
            <Button
              variant="danger"
              disabled={occupe}
              onClick={() =>
                revoquer.mutate(salle.id, {
                  onSuccess: () => {
                    setConfirmation(false);
                    setLien(null);
                  },
                  onError: echec,
                })
              }
            >
              {t('regie:viewingRooms.lien.confirmer')}
            </Button>
          </>
        }
      />
    </div>
  );
}
